import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Database,
  FolderOpen,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Copy,
  Check,
  Target,
  Layers,
  FileJson,
  Image,
  Box,
  Shapes,
  PersonStanding,
  Video,
  ScanText,
} from 'lucide-react';
import {
  TAO6Dataset,
  TAO6TaskType,
  TAO6DatasetFormat,
} from '@/mock/gpu-instance';

interface TAO6DatasetManagerProps {
  datasets: TAO6Dataset[];
  onSelectDataset?: (dataset: TAO6Dataset) => void;
  onViewDatasetCard?: (dataset: TAO6Dataset) => void;
  selectedDatasetId?: string;
}

const getFormatBadge = (format: TAO6DatasetFormat) => {
  switch (format) {
    case 'coco':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <FileJson className="h-3 w-3 mr-1" /> COCO JSON
        </Badge>
      );
    case 'kitti':
      return (
        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          <FolderOpen className="h-3 w-3 mr-1" /> KITTI
        </Badge>
      );
    case 'directory':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <Image className="h-3 w-3 mr-1" /> Directory
        </Badge>
      );
  }
};

const getTaskIcon = (task: TAO6TaskType) => {
  switch (task) {
    case 'object_detection':
      return <Target className="h-4 w-4 text-green-500" />;
    case 'image_classification':
      return <Layers className="h-4 w-4 text-blue-500" />;
    case 'instance_segmentation':
      return <Box className="h-4 w-4 text-purple-500" />;
    case 'semantic_segmentation':
      return <Shapes className="h-4 w-4 text-cyan-500" />;
    case 'pose_estimation':
      return <PersonStanding className="h-4 w-4 text-orange-500" />;
    case 'action_recognition':
      return <Video className="h-4 w-4 text-pink-500" />;
    case 'ocr':
      return <ScanText className="h-4 w-4 text-amber-500" />;
    default:
      return <Database className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusBadge = (status: TAO6Dataset['status']) => {
  switch (status) {
    case 'ready':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Ready
        </Badge>
      );
    case 'processing':
      return (
        <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Processing
        </Badge>
      );
    case 'error':
      return (
        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
          <AlertCircle className="h-3 w-3 mr-1" /> Error
        </Badge>
      );
  }
};

const TAO6DatasetManager = ({
  datasets,
  onSelectDataset,
  onViewDatasetCard,
  selectedDatasetId,
}: TAO6DatasetManagerProps) => {
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [filterTask, setFilterTask] = useState<string>('all');
  const [filterFormat, setFilterFormat] = useState<string>('all');

  const handleCopyPath = async (path: string) => {
    try {
      await navigator.clipboard.writeText(path);
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const filteredDatasets = datasets.filter((dataset) => {
    if (filterTask !== 'all' && dataset.task !== filterTask) return false;
    if (filterFormat !== 'all' && dataset.format !== filterFormat) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterTask} onValueChange={setFilterTask}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by task" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="object_detection">Object Detection</SelectItem>
            <SelectItem value="image_classification">Image Classification</SelectItem>
            <SelectItem value="instance_segmentation">Instance Segmentation</SelectItem>
            <SelectItem value="semantic_segmentation">Semantic Segmentation</SelectItem>
            <SelectItem value="pose_estimation">Pose Estimation</SelectItem>
            <SelectItem value="action_recognition">Action Recognition</SelectItem>
            <SelectItem value="ocr">OCR</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterFormat} onValueChange={setFilterFormat}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by format" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Formats</SelectItem>
            <SelectItem value="coco">COCO JSON</SelectItem>
            <SelectItem value="kitti">KITTI</SelectItem>
            <SelectItem value="directory">Directory</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Dataset List */}
      <div className="space-y-3">
        {filteredDatasets.map((dataset) => (
          <Card
            key={dataset.id}
            className={`cursor-pointer transition-all hover:border-primary/50 ${
              selectedDatasetId === dataset.id ? 'border-primary ring-1 ring-primary' : ''
            }`}
            onClick={() => onViewDatasetCard?.(dataset)}
          >
            <CardContent className="pt-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted">
                    <Database className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {dataset.name}
                      {getStatusBadge(dataset.status)}
                    </div>
                    <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                      {getTaskIcon(dataset.task)}
                      <span className="capitalize">
                        {dataset.task.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
                {getFormatBadge(dataset.format)}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Train</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.trainCount.toLocaleString()}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Val</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.valCount.toLocaleString()}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Classes</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.classCount}
                  </div>
                </div>
                <div className="p-2 bg-muted/50 rounded text-center">
                  <div className="text-xs text-muted-foreground">Size</div>
                  <div className="font-mono font-medium text-sm">
                    {dataset.totalSize}
                  </div>
                </div>
              </div>

              {/* Classes Preview */}
              <div className="mb-3">
                <div className="text-xs text-muted-foreground mb-1">Classes:</div>
                <div className="flex flex-wrap gap-1">
                  {dataset.classes.slice(0, 5).map((cls) => (
                    <Badge key={cls} variant="secondary" className="text-xs">
                      {cls}
                    </Badge>
                  ))}
                  {dataset.classes.length > 5 && (
                    <Badge variant="outline" className="text-xs">
                      +{dataset.classes.length - 5} more
                    </Badge>
                  )}
                </div>
              </div>

              {/* Path */}
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded">
                <code className="text-xs text-muted-foreground truncate">
                  {dataset.path}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPath(dataset.path);
                  }}
                >
                  {copiedPath === dataset.path ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredDatasets.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="pt-6 text-center">
              <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No datasets match your filters</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TAO6DatasetManager;
